// @ts-strict-ignore
import { ProjectModel } from '@project/model/project.model';
import { Euler, Vector3 } from 'three';
import { CompositionContainer } from '@3d-scene/model3d/composition-container';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CopiedArticleInformation } from '@project/components/article/model/copied-article-information';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { CloneUtils } from '@shared/util/clone-utils';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { ArticleRefreshData } from '@article/service/model/article-refresh-data';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { Log } from '@shared/debug/log';
import { ProjectArticleService } from '@project/service/project-article.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';

export abstract class BasePasteOperationHelper {

  protected _startPoint: Vector3;
  protected _compositionContainer: CompositionContainer;

  protected _createdArticleGuids: string[] = [];

  constructor(
    protected articleManager: ArticleManager,
    private projectModel: ProjectModel,
    private projectArticleService: ProjectArticleService,
    private articleContainerLoader: ArticleContainerLoader,
    private articleContainerFactory: ArticleContainerFactory,
    private detachArticleUsageService: DetachArticleUsageService
  ) { }

  public async executePasteArticles(articleInfos: CopiedArticleInformation[]): Promise<string[]> {
    if (articleInfos && articleInfos.length > 0) {
      const refreshData: ArticleRefreshData = new ArticleRefreshData();
      const createdContainers: ArticleContainer[] = [];
      for (const articleInformation of articleInfos) {
        const createdContainer: ArticleContainer = this.createArticleContainerByInfo(articleInformation, refreshData,
          this._startPoint, this._compositionContainer.compositionGuid);
        createdContainers.push(createdContainer);
      }
      await this.attachContainersToComposition(createdContainers, refreshData);
    }
    return this._createdArticleGuids;
  }

  protected createArticleContainerByInfo(articleInfo: CopiedArticleInformation, refreshData: ArticleRefreshData,
                                         startPoint: Vector3, compositionId: string): ArticleContainer {
    const articleUsage: ArticleUsageVO = CloneUtils.cloneArticleUsageVO(articleInfo.articleUsage) as ArticleUsageVO;
    const position: Vector3 = startPoint.clone().add(articleInfo.offsetFromCenter);
    const rotation: Euler = articleInfo.rotation.clone();

    this.projectArticleService.storeArticleUsage(articleUsage);

    const articleContainer: ArticleContainer = this.articleContainerFactory.getNewInstance();
    articleContainer.articleGUID = articleUsage.id;
    articleContainer.rotateByEuler(rotation);
    articleContainer.moveTo(position.x, position.y, position.z);

    return articleContainer;
  }

  private async attachContainersToComposition(createdContainers: ArticleContainer[], refreshData: ArticleRefreshData): Promise<string[]> {
    for (const containerToCreate of createdContainers) {
      await this._compositionContainer.attachArticleContainer(containerToCreate, this.forceArticleRefreshOnAttach(), false);
    }

    if (refreshData.dynamicArticlesInfo) {
      this.projectModel.updateArticleInfoInCurrentProject(refreshData.dynamicArticlesInfo);
    }
    try {
      await this.articleContainerLoader.refreshContainers(createdContainers, refreshData);
      ArrayUtils.mergeArrays(this._createdArticleGuids, ArticleContainerUtils.extractArticleGUIDs(createdContainers));
      return this._createdArticleGuids;
    } catch (error) {
      Log.error('Could not refresh container: ', error);
      await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(ArticleContainerUtils.extractArticleGUIDs(createdContainers));
      throw error;
    }
  }

  protected forceArticleRefreshOnAttach(): boolean {
    return true;
  }

  protected abstract getMessageType(): string;

  public clear(): void {
    this._startPoint = null;
    this._compositionContainer = null;

    this._createdArticleGuids = [];
  }
}
